/**
 * Slim confirmation after the learner switches light/dark mode or language.
 * Painted in the current mode, and kept shorter than public toasts.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Languages, LayoutList, Moon, Smile, Sun } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { localeDirection, LOCALE_USER_CHANGED_EVENT, type AppLocale } from '../../i18n';
import { THEME_USER_CHANGED_EVENT, useTheme, type Theme } from '../../hooks/useTheme';
import { useAppLocale } from '../../providers/LocaleProvider';
import {
  LEARNER_HOME_DESIGN_USER_CHANGED_EVENT,
  type LearnerHomeDesign,
} from '../../utils/learnerHomeDesign';

const VISIBLE_MS = 2000;
const EXIT_MS = 220;

interface ChromeToast {
  id: number;
  phase: 'in' | 'out';
  kind: 'theme' | 'locale' | 'design';
  theme?: Theme;
  locale?: AppLocale;
  design?: LearnerHomeDesign;
}

function pillMessage(
  toast: ChromeToast,
  t: (key: string, options?: { defaultValue?: string }) => string,
  uiLocale: AppLocale
): string {
  const lang = toast.kind === 'locale' && toast.locale ? toast.locale : uiLocale;
  const fa = lang === 'fa';
  if (toast.kind === 'locale') {
    return toast.locale === 'fa'
      ? t('persianEnabled', { defaultValue: fa ? 'فارسی فعال شد' : 'Persian enabled' })
      : t('englishEnabled', { defaultValue: fa ? 'انگلیسی فعال شد' : 'English enabled' });
  }
  if (toast.kind === 'design') {
    return toast.design === 'mme'
      ? t('simpleThemeEnabled', { defaultValue: fa ? 'قالب ساده فعال شد' : 'Simple theme enabled' })
      : t('advancedThemeEnabled', { defaultValue: fa ? 'قالب پیشرفته فعال شد' : 'Advanced theme enabled' });
  }
  return toast.theme === 'dark'
    ? t('darkModeEnabled', { defaultValue: fa ? 'حالت تاریک فعال شد' : 'Dark mode enabled' })
    : t('lightModeEnabled', { defaultValue: fa ? 'حالت روشن فعال شد' : 'Light mode enabled' });
}

export const ThemeModeToast: React.FC = () => {
  const { t } = useTranslation('common');
  const { direction, locale } = useAppLocale();
  const { isDark } = useTheme();
  const [toast, setToast] = useState<ChromeToast | null>(null);
  const hideTimer = useRef<number | null>(null);
  const removeTimer = useRef<number | null>(null);
  const remainingMs = useRef(VISIBLE_MS);
  const tickStart = useRef(0);
  const paused = useRef(false);

  const clearHideTimer = () => {
    if (hideTimer.current != null) {
      window.clearTimeout(hideTimer.current);
      hideTimer.current = null;
    }
  };

  const beginExit = useCallback(() => {
    clearHideTimer();
    paused.current = false;
    setToast((current) => {
      if (!current || current.phase === 'out') return current;
      return { ...current, phase: 'out' };
    });
  }, []);

  const scheduleHide = useCallback((ms: number) => {
    clearHideTimer();
    remainingMs.current = ms;
    tickStart.current = Date.now();
    hideTimer.current = window.setTimeout(() => {
      hideTimer.current = null;
      beginExit();
    }, ms);
  }, [beginExit]);

  useEffect(() => {
    const show = (next: Pick<ChromeToast, 'kind' | 'theme' | 'locale' | 'design'>) => {
      if (removeTimer.current != null) {
        window.clearTimeout(removeTimer.current);
        removeTimer.current = null;
      }
      paused.current = false;
      setToast((current) => ({
        id: (current?.id ?? 0) + 1,
        phase: 'in',
        kind: next.kind,
        ...(next.theme ? { theme: next.theme } : {}),
        ...(next.locale ? { locale: next.locale } : {}),
        ...(next.design ? { design: next.design } : {}),
      }));
      scheduleHide(VISIBLE_MS);
    };

    const onThemeChanged = (event: Event) => {
      const next = (event as CustomEvent<Theme>).detail;
      if (next !== 'dark' && next !== 'light') return;
      show({ kind: 'theme', theme: next });
    };

    const onLocaleChanged = (event: Event) => {
      const next = (event as CustomEvent<AppLocale>).detail;
      if (next !== 'en' && next !== 'fa') return;
      show({ kind: 'locale', locale: next });
    };

    const onDesignChanged = (event: Event) => {
      const next = (event as CustomEvent<LearnerHomeDesign>).detail;
      if (next !== 'classic' && next !== 'mme') return;
      show({ kind: 'design', design: next });
    };

    window.addEventListener(THEME_USER_CHANGED_EVENT, onThemeChanged);
    window.addEventListener(LOCALE_USER_CHANGED_EVENT, onLocaleChanged);
    window.addEventListener(LEARNER_HOME_DESIGN_USER_CHANGED_EVENT, onDesignChanged);
    return () => {
      window.removeEventListener(THEME_USER_CHANGED_EVENT, onThemeChanged);
      window.removeEventListener(LOCALE_USER_CHANGED_EVENT, onLocaleChanged);
      window.removeEventListener(LEARNER_HOME_DESIGN_USER_CHANGED_EVENT, onDesignChanged);
      clearHideTimer();
      if (removeTimer.current != null) window.clearTimeout(removeTimer.current);
    };
  }, [scheduleHide]);

  useEffect(() => {
    if (!toast || toast.phase !== 'out') return;
    removeTimer.current = window.setTimeout(() => {
      removeTimer.current = null;
      setToast((current) => (current?.phase === 'out' ? null : current));
    }, EXIT_MS);
    return () => {
      if (removeTimer.current != null) {
        window.clearTimeout(removeTimer.current);
        removeTimer.current = null;
      }
    };
  }, [toast]);

  if (!toast || typeof document === 'undefined') return null;

  const dark = toast.kind === 'theme' ? toast.theme === 'dark' : isDark;
  const message = pillMessage(toast, t, locale);
  const textDir = toast.kind === 'locale' && toast.locale ? localeDirection(toast.locale) : direction;

  const pill = (
    <div
      className="pointer-events-none fixed left-1/2 z-[2147482500] -translate-x-1/2"
      style={{ top: 'calc(4.25rem + 0.75rem)' }}
    >
      <div
        key={toast.id}
        role="status"
        dir={textDir}
        {...(toast.kind === 'theme'
          ? { 'data-theme-mode-toast': toast.theme }
          : toast.kind === 'locale'
            ? { 'data-locale-toast': toast.locale }
            : { 'data-design-toast': toast.design })}
        onClick={beginExit}
        onPointerEnter={() => {
          if (toast.phase !== 'in' || paused.current || hideTimer.current == null) return;
          paused.current = true;
          window.clearTimeout(hideTimer.current);
          hideTimer.current = null;
          remainingMs.current = Math.max(700, remainingMs.current - (Date.now() - tickStart.current));
        }}
        onPointerLeave={() => {
          if (!paused.current || toast.phase !== 'in') return;
          paused.current = false;
          scheduleHide(remainingMs.current);
        }}
        className={`theme-mode-toast pointer-events-auto flex h-8 max-w-[calc(100vw-2rem)] select-none items-center gap-1.5 rounded-full border px-3 text-[13px] font-medium leading-none backdrop-blur-lg ${
          toast.phase === 'out' ? 'theme-mode-toast-exit' : 'theme-mode-toast-enter'
        }`}
        style={{
          background: dark ? 'rgba(28, 23, 20, 0.26)' : 'rgba(255, 253, 248, 0.28)',
          color: dark ? '#f6efe6' : '#1c1714',
          borderColor: dark ? 'rgba(246, 239, 230, 0.14)' : 'rgba(28, 23, 20, 0.1)',
          boxShadow: dark
            ? '0 10px 24px rgba(0, 0, 0, 0.28)'
            : '0 10px 22px rgba(28, 23, 20, 0.1)',
        }}
      >
        {toast.kind === 'locale' ? (
          <Languages className={`h-3.5 w-3.5 shrink-0 ${dark ? 'text-amber-200' : 'text-amber-600'}`} aria-hidden />
        ) : toast.kind === 'design' ? (
          toast.design === 'mme' ? (
            <Smile className={`h-3.5 w-3.5 shrink-0 ${dark ? 'text-amber-200' : 'text-amber-600'}`} aria-hidden />
          ) : (
            <LayoutList className={`h-3.5 w-3.5 shrink-0 ${dark ? 'text-amber-200' : 'text-amber-600'}`} aria-hidden />
          )
        ) : dark ? (
          <Moon className="h-3.5 w-3.5 shrink-0 text-amber-200" aria-hidden />
        ) : (
          <Sun className="h-3.5 w-3.5 shrink-0 text-amber-600" aria-hidden />
        )}
        <span className="truncate">{message}</span>
      </div>
    </div>
  );

  return createPortal(pill, document.body);
};
